import React, { Component } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import { IEmployerFilterParams, IEmployerList } from '../../Models';
import { EmployerService } from '../../Services';
import InfiniteScroll from 'react-infinite-scroller';
import { EmployersFilter } from '../../Components';
import { EmployerArticle } from './EmployerArticle';

interface IEmployersListWidgetState {
    employersList: IEmployerList;
    employersFilterParams: IEmployerFilterParams;
    hasMore: boolean;
    isloading: boolean;
}

interface IEmployersListWidgetProps {
    description: string;
    introduction: string;
    employerDetailPage: string;
    homepageUrl: string;
    dictEmployersTitle: string;
    dictSearch: string;
    dictSearchEmployer: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictEmployersWithOffers: string;
    dictOtherEmployers: string;
}

export class EmployersListWidget extends Component<
    IEmployersListWidgetProps,
    IEmployersListWidgetState
> {
    private cancellationToken: AbortController | null = null;

    constructor(props: IEmployersListWidgetProps) {
        super(props);

        this.state = {
            employersList: {
                employers: [],
                employersWithOffers: [],
                employersCount: 0,
            },
            employersFilterParams: {
                name: '',
                page: 0,
                labeledYears: [],
            },
            hasMore: true,
            isloading: true,
        };
    }

    componentDidMount() {
        void this.getEmployerList();
    }

    componentWillUnmount() {
        if (this.cancellationToken) {
            this.cancellationToken.abort();
        }
    }

    // ✔ Safe version with abort + ESLint-safe floating promise
    getEmployerList = async (
        searchParams: IEmployerFilterParams = this.state.employersFilterParams
    ) => {
        if (this.cancellationToken) {
            this.cancellationToken.abort();
        }

        this.cancellationToken = new AbortController();

        try {
            const employersList = await EmployerService.getAllEmployers(
                searchParams,
                this.cancellationToken.signal
            );

            this.setState({
                employersList,
                employersFilterParams: {
                    ...searchParams,
                    page: searchParams.page + 1,
                },
                hasMore:
                    employersList.employers.length <
                    employersList.employersCount,
                isloading: false,
            });
        } catch (e) {
            if (!(e instanceof DOMException && e.name === 'AbortError')) {
                console.error(e);
            }
        }
    };

    getEmployerMoreData = async () => {
        const { employersFilterParams, employersList } = this.state;

        try {
            const nextPageParams = { ...employersFilterParams };
            const employersListMore =
                await EmployerService.getAllEmployers(nextPageParams);

            const newEmployers = employersListMore.employers || [];
            const combinedEmployers = [
                ...employersList.employers,
                ...newEmployers,
            ];

            const totalCount =
                employersListMore.employersCount ||
                employersList.employersCount;

            this.setState({
                employersList: {
                    ...employersList,
                    employers: combinedEmployers,
                    employersCount: totalCount,
                },
                employersFilterParams: {
                    ...employersFilterParams,
                    page: employersFilterParams.page + 1,
                },
                hasMore:
                    combinedEmployers.length < totalCount &&
                    newEmployers.length > 0,
            });
        } catch {
            this.setState({ hasMore: false });
        }
    };

    fetchMoreData = () => {
        const { hasMore, isloading } = this.state;
        if (!hasMore || isloading) return;

        void this.getEmployerMoreData();
    };

    // ✔ no-unbound-method safe update
    updateEmployersFilterParams = (
        employersFilterParams: IEmployerFilterParams
    ) => {
        this.setState(
            {
                employersFilterParams,
                isloading: true,
            },
            () => {
                void this.getEmployerList(employersFilterParams);
            }
        );
    };

    render() {
        const { employersList, isloading, hasMore } = this.state;

        return (
            <>
                <div className="banner banner--colored banner--colored__employers">
                    <EmployersFilter
                        filterEmployersList={this.updateEmployersFilterParams}
                        employersTitle={this.props.dictEmployersTitle}
                        search={this.props.dictSearch}
                        searchEmployer={this.props.dictSearchEmployer}
                    />
                </div>

                <div className="container">
                    <div className="row intro no-gutters">
                        <div className="col-12">
                            <p>{this.props.description}</p>
                        </div>

                        {this.props.introduction && (
                            <div className="col-12" data-test="intro">
                                <p>{this.props.introduction}</p>
                            </div>
                        )}
                    </div>
                </div>

                {employersList.employersWithOffers.length > 0 && (
                    <section className="highlight-section">
                        <div className="container">
                            <h2>{this.props.dictEmployersWithOffers}</h2>
                            <div className="row cards">
                                {employersList.employersWithOffers.map(
                                    (employer) => (
                                        <EmployerArticle
                                            key={employer.id}
                                            {...employer}
                                            homepageUrl={this.props.homepageUrl}
                                            employerDetailPage={
                                                this.props.employerDetailPage
                                            }
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </section>
                )}

                <section className="last">
                    <div className="container">
                        <h2>{this.props.dictOtherEmployers}</h2>

                        {isloading && (
                            <div className="lds-ripple">
                                <div></div>
                                <div></div>
                            </div>
                        )}

                        {employersList.employers.length === 0 && !isloading && (
                            <div className="empty-state">
                                <i className="icon-no-results"></i>
                                <p>
                                    <b>{this.props.dictNoResultTitle}</b>
                                </p>
                                <p>{this.props.dictNoResultDescription}</p>
                            </div>
                        )}

                        <InfiniteScroll
                            loadMore={this.fetchMoreData}
                            hasMore={hasMore}
                            loader={
                                <div className="lds-ripple" key="loader">
                                    <div></div>
                                    <div></div>
                                </div>
                            }
                            initialLoad={false}
                        >
                            <div className="row cards">
                                {employersList.employers.map((employer) => (
                                    <EmployerArticle
                                        key={employer.id}
                                        {...employer}
                                        homepageUrl={this.props.homepageUrl}
                                        employerDetailPage={
                                            this.props.employerDetailPage
                                        }
                                    />
                                ))}
                            </div>
                        </InfiniteScroll>
                    </div>
                </section>
            </>
        );
    }
}

WidgetHelper.register(EmployersListWidget, 'employers-list-widget');
